import { Box, Text } from '@chakra-ui/react'
import { Order, OrderPaymentStatus, PickupStatus } from '@leid/api-orders'

import { OrderItemInfo } from 'appTypes/orders'
import ProductImageView from 'components/ui/ItemCard/components/ItemCardImage'
import theme from 'styles/theme'

import OrderItemActionButtons from './components/OrderItemActionButtons'
import OrderItemPickupControl from './components/OrderItemPickupControl'
import OrderItemPickupInfo from './components/OrderItemPickupInfo'
import OrderItemPickupStatus from './components/OrderItemPickupStatus'
import OrderItemPrice from './components/OrderItemPrice'
import { JSX } from 'react'

type Props = {
  orderIsLoading: boolean
  orderItemInfo: OrderItemInfo
  order: Order
  orderItems: OrderItemInfo[]
  refetchOrder: () => void
  isPublicPage: boolean
}

const IndividualOrderItemInfo = (props: Props): JSX.Element | null => {
  const { orderItemInfo, refetchOrder, orderIsLoading, order, orderItems, isPublicPage } = props
  const { orderItem } = orderItemInfo

  const isOrderItemActive = (): boolean => {
    if (orderItem.productData && orderItem.productData.complementary) {
      // No buttons for consumables
      return false
    }

    if (
      orderItem.pickupStatus === PickupStatus.RETURNED ||
      orderItem.pickupStatus === PickupStatus.INSPECTED ||
      orderItem.pickupStatus === PickupStatus.COMPLETED
    ) {
      return false
    }

    if (orderItem.status === OrderPaymentStatus.canceled || orderItem.status === OrderPaymentStatus.paidAndCanceled) {
      return false
    }

    return true
  }

  const products = orderItem.productData?.products
  if (!products) {
    return null
  }

  const isBundle = products.length >= 2
  const singleProduct = !isBundle ? products[0] : undefined

  const getBackgroundColor = (): string => {
    if (
      orderItem.pickupStatus === PickupStatus.RETURNED ||
      orderItem.pickupStatus === PickupStatus.INSPECTED ||
      orderItem.pickupStatus === PickupStatus.COMPLETED
    ) {
      return theme.token('colors.leid.lightGrnn')
    }

    if (orderItem.status === OrderPaymentStatus.canceled || orderItem.status === OrderPaymentStatus.paidAndCanceled) {
      return theme.token('colors.leid.lightGrey')
    }

    return theme.token('colors.white')
  }

  const getBorderColor = (): string => {
    if (orderItem.status === OrderPaymentStatus.canceled || orderItem.status === OrderPaymentStatus.paidAndCanceled) {
      return theme.token('colors.leid.grey')
    }

    return theme.token('colors.leid.grnn')
  }

  const isActive = isOrderItemActive()

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      gridGap="24px"
      padding="16px 20px"
      borderRadius="big"
      border="1px solid"
      borderColor={getBorderColor()}
      backgroundColor={getBackgroundColor()}
      width="100%"
    >
      <Box display="flex" flexDirection="column" alignItems="center" gridGap="24px" alignSelf="stretch">
        <Box display="flex" flexDirection="column" alignItems="center" gridGap="12px" alignSelf="stretch">
          {/* Header */}
          <Box width="100%" display="flex" justifyContent="center" alignItems="flex-start" gridGap="4px">
            <Text width="100%" fontWeight={700} fontSize="md" color="text.header">
              {orderItem.productData?.name ?? ''}
            </Text>
          </Box>

          {singleProduct && (
            <ProductImageView
              productDefaultImage={singleProduct.imageUrl}
              imageVersions={singleProduct.imageVersions ?? []}
              desiredVersion="medium"
              altText={singleProduct.name}
              htmlWidth="400px"
              htmlHeight="300px"
            />
          )}

          <OrderItemPrice orderItem={orderItem} />

          {orderItem.status !== OrderPaymentStatus.canceled &&
            orderItem.status !== OrderPaymentStatus.paidAndCanceled &&
            orderItem.pickupStatus !== PickupStatus.RETURNED &&
            orderItem.pickupStatus !== PickupStatus.COMPLETED && (
              <>
                <OrderItemPickupStatus orderItem={orderItem} orderIsLoading={orderIsLoading} />
              </>
            )}

          <OrderItemPickupControl
            orderItemInfo={orderItemInfo}
            orderItems={orderItems.map((s) => s.orderItem)}
            order={order}
            refetchOrder={refetchOrder}
            orderIsLoading={orderIsLoading}
            isPublicPage={isPublicPage}
          />

          <OrderItemPickupInfo orderItemInfo={orderItemInfo} />
        </Box>

        {isActive && <OrderItemActionButtons orderItemInfo={orderItemInfo} refetchOrder={refetchOrder} />}
      </Box>
    </Box>
  )
}

export default IndividualOrderItemInfo
